/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Hidden, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import type { UseCurriculum_AcademicEventFragment as AcademicEvent } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import bannerBackgroundImage from 'images/degree-home/banner-background.svg';
import calendarImage from 'images/degree-home/calendar.png';

import _t from 'i18n!nls/degree-home';

const styles = {
  contentContainer: () => css`
    background-image: url(${bannerBackgroundImage});
    background-position: right;
    background-repeat: no-repeat;
    background-size: contain;
    padding: var(--cds-spacing-400);
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
    border-radius: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      background-size: cover;
      padding: var(--cds-spacing-300);
    }
  `,
  bannerHeading: () => css`
    margin-bottom: var(--cds-spacing-100);
    font-weight: 400;
    line-height: 24px;
    font-size: 28px;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    font-weight: 600;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    line-height: 36px;
  `,
  bannerDescription: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,
  enrollBtn: () => css`
    margin-top: var(--cds-spacing-300);
  `,
  bottomSpace: css`
    margin-bottom: 1.5rem;
  `,
  imgGrid: css`
    display: flex;
    justify-content: right;
    align-items: center;
  `,
  iconSpacing: () => css`
    margin-left: var(--cds-spacing-100);
  `,
  background: () => css`
    background-color: var(--cds-color-blue-25);
    border-radius: var(--cds-spacing-100);
    ${breakpoints.down('xs')} {
      border-radius: 0;
    }
  `,
};

const NudgeBanner = ({ academicEvent }: { academicEvent: AcademicEvent }) => {
  return (
    <TrackedDiv
      trackingName="nudge_banner"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Grid aria-label={_t('Nudge Banner')} css={styles.background}>
        <Grid container alignContent="center" css={styles.contentContainer}>
          <Grid item xs={12}>
            <Hidden smUp>
              <img src={calendarImage} alt={_t('Calendar icon')} width="74" css={styles.bottomSpace} />
            </Hidden>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography variant="h1" color="body" css={styles.bannerHeading}>
              {academicEvent.eventTitle}
            </Typography>
            <Typography2 component="p" css={styles.bannerDescription} variant="bodyPrimary">
              {academicEvent.description}
            </Typography2>
            {!!academicEvent.actionLink && (
              <Button
                variant="primary"
                size="small"
                component={TrackedLink2}
                href={academicEvent.actionLink}
                withVisibilityTracking
                aria-label={_t('Enroll Now')}
                trackingName="nudge_banner_cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Enroll Now')}
                <ExternalLinkIcon css={styles.iconSpacing} />
              </Button>
            )}
          </Grid>
          <Grid item xs={12} sm={3} css={styles.imgGrid}>
            <Hidden xsDown>
              <img src={calendarImage} alt={_t('calendar icon')} width="192" />
            </Hidden>
          </Grid>
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};

export default NudgeBanner;
